import { Tabs } from 'antd'
import { AxiosError } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { ZodError } from 'zod'

import Loader from '@/components/Atoms/Loader/Loader'
import CharacteristicCreationModal from '@/components/EntityCreationModals/CharacteristicCreationModal/CharacteristicCreationModal'
import { GENERAL_ERRORS_MESSAGES, TYPE_LABELS } from '@/constants'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import type { UnitOfMeasurementInterface } from '@/types'
import api from '@/utils/api/api'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import {
  getStatusBadge,
  isCatalogApprover,
} from '@/utils/catalog/approvalStatus'

import CatalogApprovalVirtualTable, {
  type CatalogApprovalColumn,
} from '../CatalogApproval/CatalogApprovalVirtualTable/CatalogApprovalVirtualTable'
import CharacteristicDetailsModal from '../shared/CharacteristicDetailsModal/CharacteristicDetailsModal'
import DictionaryPageHeader from '../shared/DictionaryPageHeader/DictionaryPageHeader'
import cn from './CharacteristicsCatalog.module.scss'

type CharacteristicRecord = {
  id: string
  code: number
  description: string
  status: 'DRAFT' | 'APPROVED' | 'REJECTED'
  valueType: 'NUMERIC' | 'BOOLEAN' | 'REFERENCE' | 'STRING'
  useUnitOfMeasurement: boolean
  unitOfMeasurementDto?: UnitOfMeasurementInterface | null
}

function CharacteristicsCatalog() {
  const [openCreateModal, setOpenCreateModal] = useState(false)
  const [loadingError, setLoadingError] = useState('')
  const [loading, setLoading] = useState(false)
  const [allCharacteristics, setAllCharacteristics] = useState<
    CharacteristicRecord[]
  >([])
  const [newCharacteristics, setNewCharacteristics] = useState<
    CharacteristicRecord[]
  >([])
  const [selectedCharacteristic, setSelectedCharacteristic] =
    useState<CharacteristicRecord | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)

  const currentUser = useUserInfoStore((state) => state.currentUser)
  const isApprover = isCatalogApprover(currentUser?.roles)

  const loadData = useCallback(async () => {
    setLoading(true)
    setLoadingError('')

    try {
      const requests = [api.get('/main/characteristics')]
      if (isApprover) {
        requests.push(api.get('/main/characteristics/moderation'))
      }

      const responses = await Promise.all(requests)
      const [allResponse, moderationResponse] = responses

      setAllCharacteristics(allResponse.data.characteristics ?? [])
      setNewCharacteristics(moderationResponse?.data.characteristics ?? [])
    } catch (error) {
      if (error instanceof AxiosError) {
        setLoadingError(getGeneralErrorMessage(error))
      } else if (error instanceof ZodError) {
        setLoadingError(GENERAL_ERRORS_MESSAGES.ZOD_ERROR)
      } else {
        setLoadingError(GENERAL_ERRORS_MESSAGES.DEFAULT_ERROR)
      }
    } finally {
      setLoading(false)
    }
  }, [isApprover])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const openDetails = useCallback((characteristic: CharacteristicRecord) => {
    setSelectedCharacteristic(characteristic)
    setDetailsOpen(true)
  }, [])

  const columns = useMemo<CatalogApprovalColumn<CharacteristicRecord>[]>(
    () => [
      {
        id: 'code',
        accessorKey: 'code',
        header: 'Код',
        width: '120px',
        cell: (info) => info.getValue<number>(),
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: 'Наименование',
        width: 'minmax(320px, 2fr)',
        cell: (info) => info.getValue<string>(),
      },
      {
        id: 'unit',
        header: 'Ед. изм.',
        width: '180px',
        cell: (info) =>
          info.row.original.unitOfMeasurementDto?.description || '—',
      },
      {
        id: 'type',
        accessorKey: 'valueType',
        header: 'Тип',
        width: '160px',
        cell: (info) => {
          const value = info.getValue<CharacteristicRecord['valueType']>()
          return TYPE_LABELS[value] ?? value
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Статус',
        width: '180px',
        cell: (info) =>
          getStatusBadge(info.row.original.status) ?? 'Утверждена',
      },
      {
        id: 'action',
        header: 'Действие',
        width: '130px',
        cell: (info) => (
          <button
            type="button"
            className={cn.rowActionButton}
            onClick={(event) => {
              event.stopPropagation()
              openDetails(info.row.original)
            }}
          >
            Открыть
          </button>
        ),
      },
    ],
    [openDetails]
  )

  return (
    <section className={cn.characteristicsCatalog}>
      <CharacteristicCreationModal
        open={openCreateModal}
        setOpen={setOpenCreateModal}
        onSuccess={() => {
          void loadData()
        }}
      />

      <CharacteristicDetailsModal
        open={detailsOpen}
        item={selectedCharacteristic}
        onClose={() => {
          setDetailsOpen(false)
          setSelectedCharacteristic(null)
        }}
        onChanged={loadData}
        isApprover={isApprover}
      />

      {!loading && !loadingError && (
        <div className={cn.inner}>
          <DictionaryPageHeader
            title="Характеристики"
            activePage="characteristics"
            actionButton={
              <button
                className={cn.actionButton}
                onClick={() => setOpenCreateModal(true)}
              >
                Добавить характеристику
              </button>
            }
          />

          <div className={cn.tableWrap}>
            <Tabs
              items={[
                ...(isApprover
                  ? [
                      {
                        key: 'new',
                        label: 'Новые',
                        children: (
                          <CatalogApprovalVirtualTable
                            columns={columns}
                            data={newCharacteristics}
                            emptyMessage="Новые характеристики не найдены"
                            minTableWidth="1090px"
                            estimateRowHeight={58}
                            rowKey={(record) => record.id}
                            onRowClick={openDetails}
                          />
                        ),
                      },
                    ]
                  : []),
                {
                  key: 'all',
                  label: 'Все характеристики',
                  children: (
                    <CatalogApprovalVirtualTable
                      columns={columns}
                      data={allCharacteristics}
                      emptyMessage="Характеристики не найдены"
                      minTableWidth="1090px"
                      estimateRowHeight={58}
                      rowKey={(record) => record.id}
                      onRowClick={openDetails}
                    />
                  ),
                },
              ]}
            />
          </div>
        </div>
      )}

      {!loading && !!loadingError && (
        <div className={cn.inner}>
          <p className={cn.errorMessage}>{loadingError}</p>
        </div>
      )}

      <Loader loaderType="modal" isModalOpen={loading} />
    </section>
  )
}

export default CharacteristicsCatalog
